import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { groupBy, cloneDeep } from 'es-toolkit/compat';

import { Transaction, TableMapContextType, TableView, Make } from "./types";
import { GetLocationDataReturn } from "@/migrated-widgets/table-map/services/PortalV2/types";

import { groupSalesDataByMakeModel, groupSalesDataByZipCodeNew, groupSalesDataByZipCodeUsed } from "@/migrated-widgets/table-map/utils/grouping";
import { campaignColorsDef, locationProducts, productIds } from "./constants";
import { useTableMapData } from "./hooks/useTableMapData";
import { useGlobal } from "@/migrated-widgets/table-map/contexts/GlobalState";
import { type } from "@/migrated-widgets/table-map/utils/types";
import { useTransactionMarkers } from "./hooks/useTransactionMarkers";
import { useWalkieTalkie } from "@/migrated-widgets/table-map/hooks/useWalkieTalkie";
import { useWidgetData } from "@/app/widgets/WidgetFactoryContext";
import { useDealerMarkers } from "../../../hooks/shared/useDealerMarkers";
import { useCompetitorsMarkers } from "../../../hooks/shared/useCompetitorsMarkers";
import { GeoUtils } from "@/migrated-widgets/table-map/utils/geo";


const TableMapContext = createContext<TableMapContextType>({

    campaignColors: {},
    campaigns: [],
    circleMarkers: [],
    geometryFeatures: [],
    distinctCampaigns: [],
    distinctProducts: [],
    distinctDealers: [],
    currentProduct: undefined,

    dealerMarkers: [],
    loadingDealers: false,

    competitorsMarkers: [],
    loadingCompetitors: false,

    loadingLocations: false,
    locations: undefined,
    locationsBy: 'product',

    loadingTransactions: false,
    transactions: [],

    vehicleTypes: [],
    makes: [],
    models: [],
    zipCodes: [],
    dealers: [],
    onlyMap: false,
    products: [],
    zoomResetTrigger: 0,
    collapsed: false,

    showCrmLeads: false,
    setShowCrmLeads: () => { },

    setCampaigns: () => { },
    setLocationsBy: () => { },
    setVehicleTypes: () => { },
    setMakes: () => { },
    setModels: () => { },
    setZipCodes: () => { },
    setDealers: () => { },
    setOnlyMap: () => { },
    setProducts: () => { },
    triggerZoomReset: () => { },
    setCollapsed: () => { },

    showingTransactionType: 'S',
    setShowingTransactionType: () => { },

    tableView: 'byModels',
    setTableView: () => { },

    totalSales: { newSales: [], usedSales: [], newSold: 0, usedSold: 0, totalSold: 0 },
    totalLeads: 0,
    salesMarkers: [],
    repairOrdersMarkers: [],
    leadsMarkers: [],
    zipCodeNewMarkers: [],
    zipCodeUsedMarkers: [],
    walkieTalkie: { listen: () => { }, send: () => { } },

    filterCircle: null,
    setFilterCircle: () => { },
    circleZipCodes: [],
    mapZoom: 9,
    setMapZoom: () => { },

})


const TableMapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const walkieTalkie = useWalkieTalkie();
    const { setNoData } = useWidgetData();
    const [filterCircle, setFilterCircle] = useState<{ center: google.maps.LatLngLiteral; radius: number } | null>(null);
    const [mapZoom, setMapZoom] = useState<number>(9);
    const [products, setProducts] = useState<string[]>([]);
    const [campaigns, setCampaigns] = useState<string[]>([]);
    const [locationsBy, setLocationsBy] = useState<'campaign' | 'product'>('product');
    const [onlyMap, setOnlyMap] = useState(false);
    const [currentProduct, setCurrentProduct] = useState<number>();
    const [totalSales, setTotalSales] = useState<Transaction>({ newSales: [], usedSales: [], newSold: 0, usedSold: 0, totalSold: 0 });
    const [totalLeads, setTotalLeads] = useState(0);
    const [zoomResetTrigger, setZoomResetTrigger] = useState(0);
    const [collapsed, setCollapsed] = useState(false);
    const [showCrmLeads, setShowCrmLeads] = useState(false);
    const [showingTransactionType, setShowingTransactionType] = useState<'S' | 'L' | 'R'>('S');
    const [tableView, setTableView] = useState<TableView>('byModels');
    const triggerZoomReset = () => setZoomResetTrigger(zoomResetTrigger + 1);

    const {
        locations,
        loadingLocations,
        transactions,
        loadingTransactions,
    } = useTableMapData();

    const {
        dealerMarkers,
        loadingDealers
    } = useDealerMarkers();

    const {
        competitorsMarkers,
        loadingCompetitors
    } = useCompetitorsMarkers();

    const transactionsSource = useMemo(() =>
        transactions.filter((transaction) => {
            if (transaction.transactionType === 'L') {
                if (!showCrmLeads) return false;
                if (tableView === 'zipCodesNew') return transaction.vehicleType === 'NEW';
                if (tableView === 'zipCodesUsed') return transaction.vehicleType === 'USED';
            }
            return true;
        }),
        [transactions, showCrmLeads, tableView])

    const { salesMarkers, repairOrdersMarkers, leadsMarkers, dealers, zipCodes, vehicleTypes, makes, models, setDealers, setZipCodes, setVehicleTypes, setMakes, setModels } = useTransactionMarkers(transactionsSource);

    const zipCodeNewMarkers = useMemo((): Transaction[] => {
        const salesTx = transactions.filter((t) => t.transactionType === showingTransactionType);
        const filtered = dealers.length > 0 ? salesTx.filter((t) => !dealers.includes(t.clientName)) : salesTx;
        const rows = groupSalesDataByZipCodeNew(filtered);
        return rows.map((row) => {
            const tx = filtered.find((t) => t.zipcode === row.zipCode && t.latitude && t.longitude);
            return {
                newSold: row.totalSold,
                usedSold: 0,
                totalSold: row.totalSold,
                newSales: [] as Make[],
                usedSales: [] as Make[],
                zipCode: row.zipCode,
                position: tx ? { lat: tx.latitude, lng: tx.longitude } : undefined,
            };
        }).filter((m): m is Transaction & { position: google.maps.LatLngLiteral; zipCode: string } => !!(m.position?.lat && m.position?.lng && m.zipCode));
    }, [transactions, showingTransactionType, dealers]);

    const zipCodeUsedMarkers = useMemo((): Transaction[] => {
        const salesTx = transactions.filter((t) => t.transactionType === showingTransactionType);
        const filtered = dealers.length > 0 ? salesTx.filter((t) => !dealers.includes(t.clientName)) : salesTx;
        const rows = groupSalesDataByZipCodeUsed(filtered);
        return rows.map((row) => {
            const tx = filtered.find((t) => t.zipcode === row.zipCode && t.latitude && t.longitude);
            return {
                newSold: 0,
                usedSold: row.totalSold,
                totalSold: row.totalSold,
                newSales: [] as Make[],
                usedSales: [] as Make[],
                zipCode: row.zipCode,
                position: tx ? { lat: tx.latitude, lng: tx.longitude } : undefined,
            };
        }).filter((m): m is Transaction & { position: google.maps.LatLngLiteral; zipCode: string } => !!(m.position?.lat && m.position?.lng && m.zipCode));
    }, [transactions, showingTransactionType, dealers]);
    const { params } = useGlobal();

    const [circleMarkersSource, setCircleMarkersSource] = useState<
        GetLocationDataReturn["circleMarkers"]
    >([]);

    const [geometryFeaturesSource, setGeometryFeaturesSource] = useState<
        GetLocationDataReturn["geometryFeatures"]
    >([]);

    const [campaignColors, setCampaignColors] = useState<Record<string, string>>({});


    const circleMarkersByProduct = useMemo(() => {
        if (!products.length) return circleMarkersSource;
        const productsIds = products.map((product: string) => productIds[product]);

        const markers = cloneDeep(circleMarkersSource)
            .filter((marker) => marker.locations.some((location) => !productsIds.includes(productIds[location.product])));
        markers.forEach((marker) => marker.locations = marker.locations.filter((location) => !productsIds.includes(productIds[location.product])));
        return markers;
    }, [circleMarkersSource, products])


    const circleMarkersByCampaign = useMemo(() => {
        if (!campaigns.length) return circleMarkersSource;

        const markers = cloneDeep(circleMarkersSource)
            .filter((marker) => marker.locations.some((location) => !campaigns.includes(location.campaignName)))
        markers.forEach((marker) => marker.locations = marker.locations.filter((location) => !campaigns.includes(location.campaignName)));
        return markers;
    }, [circleMarkersSource, campaigns])

    const geometryFeaturesByProduct = useMemo(() => {
        if (!products.length) return geometryFeaturesSource;
        const productsIds = products.map((product: string) => productIds[product]);

        const features = cloneDeep(geometryFeaturesSource)
            .filter((feature) => feature.locations.some((location) => !productsIds.includes(productIds[location.product])));
        features.forEach((feature) => feature.locations = feature.locations.filter((location) => !productsIds.includes(productIds[location.product])));
        return features;
    }, [geometryFeaturesSource, products])


    const geometryFeaturesByCampaign = useMemo(() => {
        if (!campaigns.length) return geometryFeaturesSource;

        const features = cloneDeep(geometryFeaturesSource)
            .filter((feature) => feature.locations.some((location) => !campaigns.includes(location.campaignName)))
        features.forEach((feature) => feature.locations = feature.locations.filter((location) => !campaigns.includes(location.campaignName)));
        return features;
    }, [geometryFeaturesSource, campaigns])

    const distinctCampaigns = useMemo(() => {
        const grouped = groupBy(
            [...circleMarkersSource, ...geometryFeaturesSource]
                .flatMap((marker) => marker.locations.map((location) => location.campaignName))
                .filter((campaignName) => campaignName),
            (campaignName: string) => campaignName
        );

        return Object.keys(grouped);

    }, [circleMarkersSource, geometryFeaturesSource])

    const distinctProducts = useMemo(() => {
        const locationProductsFilter = [...circleMarkersSource, ...geometryFeaturesSource]
            .flatMap((marker) => marker.locations.map((location) => productIds[location.product]))
            .filter((location) => location)
            .map((productId) => locationProducts.find(location => location.id === productId));

        const productNames = locationProductsFilter.map((product) => product?.value)

        return type<string[]>(Array.from(new Set(productNames)));

    }, [circleMarkersSource, geometryFeaturesSource])

    const distinctDealers = useMemo(() => {
        const grouped = groupBy(transactions, (transaction) => transaction.clientName);
        return Object.keys(grouped);
    }, [transactions]);

    const circleMarkers = locationsBy === 'product' ? circleMarkersByProduct : circleMarkersByCampaign;

    const geometryFeatures = locationsBy === 'product' ? geometryFeaturesByProduct : geometryFeaturesByCampaign;

    // Zip codes whose markers touch or overlap the filter circle.
    // Uses the marker's actual pixel size (which scales with totalSold) converted
    // to meters at the current map zoom — so a larger marker is included even
    // when its center point is just outside the circle edge.
    const circleZipCodes = useMemo(() => {
        if (!filterCircle) return [];
        const markers =
            showingTransactionType === 'S' ? salesMarkers :
            showingTransactionType === 'R' ? repairOrdersMarkers :
            leadsMarkers;
        return Array.from(new Set(
            markers
                .filter((marker) => {
                    if (!marker.position?.lat || !marker.position?.lng) return false;
                    const pixelRadius = GeoUtils.getMarkerPixelRadius(marker.totalSold);
                    const mPerPx = GeoUtils.metersPerPixelAtZoom(marker.position.lat, mapZoom);
                    return GeoUtils.isInsideCircle(
                        marker.position as google.maps.LatLngLiteral,
                        filterCircle.center,
                        filterCircle.radius,
                        pixelRadius * mPerPx,
                    );
                })
                .map((marker) => marker.zipCode)
                .filter(Boolean) as string[]
        ));
    }, [filterCircle, salesMarkers, repairOrdersMarkers, leadsMarkers, showingTransactionType, mapZoom]);

    const leadsCircleZipCodes = useMemo(() => {
        if (!filterCircle) return [];
        return Array.from(new Set(
            leadsMarkers
                .filter((marker) => {
                    if (!marker.position?.lat || !marker.position?.lng) return false;
                    const pixelRadius = GeoUtils.getMarkerPixelRadius(marker.totalSold);
                    const mPerPx = GeoUtils.metersPerPixelAtZoom(marker.position.lat, mapZoom);
                    return GeoUtils.isInsideCircle(
                        marker.position as google.maps.LatLngLiteral,
                        filterCircle.center,
                        filterCircle.radius,
                        pixelRadius * mPerPx,
                    );
                })
                .map((marker) => marker.zipCode)
                .filter(Boolean) as string[]
        ));
    }, [filterCircle, leadsMarkers, mapZoom]);

    useEffect(() => {
        if (!loadingTransactions) {
            const onlySalesTransactions = transactions.filter((transaction) => transaction.transactionType === showingTransactionType);
            const sales = dealers.length > 0 ? onlySalesTransactions.filter((transaction) => !dealers.includes(transaction.clientName)) : onlySalesTransactions;
            // When circle is active, always use its results (empty circle = 0 results).
            // Only fall back to manual zip selection when no circle is active.
            const filtered = filterCircle
                ? (circleZipCodes.length ? sales.filter((t) => circleZipCodes.includes(t.zipcode)) : [])
                : (zipCodes.length ? sales.filter((t) => zipCodes.includes(t.zipcode)) : sales);
            const newSales = groupSalesDataByMakeModel(filtered, 'NEW');
            const usedSales = groupSalesDataByMakeModel(filtered, 'USED');
            const newSold = newSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            const usedSold = usedSales.reduce((acc, crr) => acc + crr.totalSold, 0);
            setTotalSales({
                newSales,
                usedSales,
                newSold,
                usedSold,
                totalSold: newSold + usedSold,
            });
        }
    }, [transactions, loadingTransactions, dealers, zipCodes, circleZipCodes, filterCircle, showingTransactionType]);

    useEffect(() => {
        const markers = filterCircle
            ? (leadsCircleZipCodes.length ? leadsMarkers.filter((m) => m.zipCode && leadsCircleZipCodes.includes(m.zipCode)) : [])
            : leadsMarkers;
        setTotalLeads(markers.reduce((acc, crr) => acc + crr.totalSold, 0));
    }, [leadsMarkers, filterCircle, leadsCircleZipCodes]);

    useEffect(() => {
        /**
         * This effect sets the products state based on the presence of the PPC product in the distinctProducts array.
         * If the PPC product exists in the distinctProducts and the locationsBy is 'product',
         * it filters out the PPC product from the distinctProducts and sets the result as the products state.
         * Otherwise, it sets the products state to be the distinctProducts array.
         */
        const PPCProduct = locationProducts.find(product => product.value === 'PPC') ?? locationProducts[0];

        const existInDistinctProducts = distinctProducts.includes(PPCProduct.value);

        if (PPCProduct.value && existInDistinctProducts && locationsBy === 'product') {
            setProducts(distinctProducts.filter(productName => PPCProduct.value !== productName));
        } else {
            setProducts(distinctProducts);
        }
    }, [distinctProducts, locationsBy]);

    useEffect(() => {
        if (locations?.circleMarkers) {
            setCircleMarkersSource(locations.circleMarkers);
        }
    }, [locations]);

    useEffect(() => {
        if (locations?.geometryFeatures) {
            setGeometryFeaturesSource(locations.geometryFeatures);
        }
    }, [locations]);

    useEffect(() => {
        if (params.product && !Number.isNaN(Number(params?.product))) {
            setLocationsBy('campaign');
            setCurrentProduct(Number(params?.product));
        }
    }, [params])

    useEffect(() => {
        const _colors: Record<string, string> = {}

        distinctCampaigns.forEach((campaignName, index) => {
            _colors[campaignName] = campaignColorsDef[index % campaignColorsDef.length]
        })

        setCampaignColors(_colors);
    }, [distinctCampaigns])


    useEffect(() => {
        if (!loadingTransactions && !loadingLocations) {
            if (transactions.length) {
                setOnlyMap(false);

                return;
            }

            if (!transactions.length && !locations?.circleMarkers?.length && !locations?.geometryFeatures?.length) {
                setNoData(true);
            } else if (!transactions.length) {
                setOnlyMap(true);
            }

        }

    }, [transactions, loadingTransactions, loadingLocations]);

    useEffect(() => {
        setModels([]);
        setMakes([]);
    }, [showingTransactionType])


    return (
        <TableMapContext.Provider value={{
            campaignColors,
            campaigns,
            circleMarkers,
            geometryFeatures,
            distinctCampaigns,
            distinctProducts,
            distinctDealers,
            currentProduct,

            dealerMarkers,
            loadingDealers,

            competitorsMarkers,
            loadingCompetitors,

            loadingLocations,
            locations,
            locationsBy,

            loadingTransactions,
            transactions,

            vehicleTypes,
            makes,
            models,
            zipCodes,
            dealers,
            onlyMap,
            products,
            zoomResetTrigger,
            collapsed,

            showCrmLeads,
            setShowCrmLeads,

            setCampaigns,
            setLocationsBy,
            setVehicleTypes,
            setMakes,
            setModels,
            setZipCodes,
            setDealers,
            setOnlyMap,
            setProducts,
            triggerZoomReset,
            setCollapsed,
            showingTransactionType,
            setShowingTransactionType,
            tableView,
            setTableView,
            totalSales,
            totalLeads,
            salesMarkers,
            repairOrdersMarkers,
            leadsMarkers,
            zipCodeNewMarkers,
            zipCodeUsedMarkers,
            walkieTalkie,
            filterCircle,
            setFilterCircle,
            circleZipCodes,
            mapZoom,
            setMapZoom,
        }}>
            {children}
        </TableMapContext.Provider>
    )
}

function useTableMapState() {
    return useContext(TableMapContext);
}

export {
    TableMapContext,
    TableMapProvider,
    useTableMapState,
};
